import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { StatusPagePiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/saas')({
  component: SaasPage,
  head: () => ({
    meta: [
      { title: 'SaaS Uptime Monitoring — Track SLAs & API Health | Uptrack' },
      { name: 'description', content: 'Monitor your SaaS with 30s checks: 99.99% SLA tracking, API health, response times, status pages for transparency.' },
      { property: 'og:title', content: 'SaaS Uptime Monitoring — Track SLAs & API Health | Uptrack' },
      { property: 'og:description', content: 'Track SLA compliance, monitor API endpoints, manage incidents. Built for SaaS teams. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-saas.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/saas' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For SaaS', item: 'https://uptrack.app/for/saas' },
          ],
        }),
      },
    ],
  }),
})

function SaasPage() {
  return (
    <FeaturePage
      eyebrow='For · SaaS teams'
      section='SaaS teams'
      title='Your SLA depends on it.'
      intro='99.99% uptime means less than 53 minutes of downtime per year. Every second counts. Uptrack monitors your app, API, and infrastructure with 30-second checks — so you catch issues before they become SLA violations.'
      featuresHeading='Built for SaaS reliability'
      features={[
        { title: '99.99% SLA tracking', desc: 'Track your actual uptime percentage over 30, 60, and 90 days. Know exactly where you stand against your SLA commitments.' },
        { title: 'API endpoint monitoring', desc: 'Monitor every critical endpoint — auth, billing, webhooks, data APIs. Each with its own check interval and alert rules.' },
        { title: 'Incident management', desc: 'Automatic incident creation on failures. Timeline tracking, status updates, and resolution notes — all in one place.' },
        { title: 'Status pages for SLA transparency', desc: 'Public or private status pages your customers can bookmark. Show real uptime numbers to build trust.' },
        { title: 'Response time trends', desc: 'Track p50, p95, and p99 response times. Spot slow endpoints before they degrade the user experience.' },
        { title: 'Alert confirmation', desc: 'Uptrack re-checks before alerting. No more false SLA violations from transient network blips.' },
      ]}
      why={{ heading: 'Your customers expect 100% uptime', body: "A customer's integration starts failing because your API endpoint is returning 500s. With 5-minute checks, that's 5 minutes of failed requests and angry support tickets. With Uptrack's 30-second checks and alert confirmation, you know in under a minute — and you know it's real, not a blip." }}
      cta={{ heading: '30-second checks on every plan', body: 'Monitor your app, API, and webhooks. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="status.example.com">
          <StatusPagePiece />
        </ProductFrame>
      }
    />
  )
}
